import {
  Badge,
  Box,
  Button,
  FileButton,
  Flex,
  Grid,
  Group,
  Input,
  MultiSelect,
  MultiSelectProps,
  Text,
  rem,
} from "@mantine/core";
import {
  useDebouncedCallback,
  useDebouncedValue,
  useDisclosure,
} from "@mantine/hooks";
import {
  IconBarcode,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconDownload,
  IconFileDownload,
  IconFileUpload,
  IconSearch,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryPos,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_DELIVERY } from "../../../config";
import { _localization } from "../../../config/location";
import { green } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";
import { SelectListItemModel } from "../../../model/SelectListItem";
import ImportSerialPreviewModal, {
  SerialImportPreview,
  readSerialImportPreview,
} from "./ImportSerialPreviewModal";

const ListSerialStocktaking = () => {
  const [searchParams] = useSearchParams();
  const initialSerial = (searchParams.get("serial") || "").trim();
  // Khởi tạo component InventoryReportList
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [data, setData] = useState<InventoryItem[]>([]);
  const [dataInvCode, setDataInvCode] = useState<SelectListItemModel[]>([]);
  const [dataItemCode, setDataItemCode] = useState<SelectListItemModel[]>([]);
  const [dataBranchCode, setDataBranchCode] = useState<any[]>([]);
  const [dataBranchIdSelect, setDataBranchIdSelect] = useState<string[]>([]);
  const [dataCatSegment4d, setDataCatSegment4d] = useState<
    SelectListItemModel[]
  >([]);
  const [searchCatSegment4d, setSearchCatSegment4d] = useState<string>("");
  const [debounceCatSegment4d] = useDebouncedValue<string>(
    searchCatSegment4d,
    500
  );

  const [visible, { toggle, close, open }] = useDisclosure(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<SerialImportPreview | null>(null);
  const resetRef = useRef<() => void>(null);

  const sizeGrid = { base: 12, sm: 6, md: 6, lg: 12 / 7 };
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState({
    keySearch: "",
    serial: initialSerial,
    branchCode: [],
    invCode: [],
    itemCode: [],
    maChungs: [],
  });

  type InventoryItem = {
    branchCode: string;
    branchName: string;
    countSoLuong: number;
    dvt: string | null;
    idBranch: number;
    idKho: number;
    maKho: string;
    maSp: string;
    soLuong: number | null;
    tenKho: string;
    tenSp: string;
    maBarcode: string;
    ngayNhap: string;
    soPhieuNhap: string;
    donGiaNhap: string;
  };

  const clearFile = () => {
    setFile(null);
    resetRef.current?.();
  };

  const handleChangeSearchValue = (value: string[] | null, key: string) => {
    setSearch((prevData) => ({
      ...prevData,
      [key]: value ? value : [],
    }));
  };

  const handleChangeSearch = (value: string | null, key: string) => {
    setSearch((prevData) => ({
      ...prevData,
      [key]: value ? value : 0,
    }));
  };

  const formatNumber = (number: number) => {
    return new Intl.NumberFormat("vi-VN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number);
  };

  const calculateDaysDifference = (date: string) => {
    const now = new Date();
    const pastDate = new Date(date);
    const differenceInTime = now.getTime() - pastDate.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);
    return formatNumber(differenceInDays);
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "STT",
        enableColumnActions: false,
        enableSorting: false,
        enableResizing: false,
        Cell: ({ row }) => <>{row?.index + 1}</>,
        size: 50,
      },
      {
        accessorKey: "soPhieuNhap",
        header: "Số phiếu nhập",
        size: 150,
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge
              style={{ cursor: "pointer" }}
              bd="1px solid blue"
              variant="light"
              color="blue"
            >
              {renderedCellValue}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "maBarcode",
        header: "Mã vạch",
      },
      {
        accessorKey: "trangThai",
        header: "Trạng thái serial",
      },
      {
        accessorKey: "ghiChuTrangThai",
        header: "Ghi chú trạng thái",
        size: 250,
      },
      {
        accessorKey: "branchCode",
        header: "Mã chi nhánh",
        size: 150,
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Button
              variant="light"
              p="4px 8px"
              radius="xl"
              size="xs"
              color="violet"
              fw="bold"
            >
              {renderedCellValue}
            </Button>
          </>
        ),
      },
      {
        accessorKey: "branchName",
        header: "Tên chi nhánh",
      },
      {
        accessorKey: "maNcc",
        header: "Mã NCC",
      },
      {
        accessorKey: "tenNcc",
        header: "Tên NCC",
      },
      {
        accessorKey: "maKho",
        header: "Mã kho",
        size: 150,
      },
      {
        accessorKey: "tenKho",
        header: "Tên kho",
        size: 200,
      },
      {
        accessorKey: "maVt",
        header: "Mã vị trí",
        size: 150,
      },
      {
        accessorKey: "tenVt",
        header: "Tên vị trí",
      },
      {
        accessorKey: "maSp",
        header: "Mã sản phẩm",
        size: 160,
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge
              radius="sm"
              variant="dot"
              size="md"
              style={{ cursor: "pointer" }}
              color={renderedCellValue === null ? "red" : green.base}
            >
              {renderedCellValue ?? "Không có mã sản phẩm"}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "maChung",
        header: "Mã chủng",
      },
      {
        accessorKey: "tenChung",
        header: "Tên chủng",
      },
      // {
      //   accessorKey: "MaNhom",
      //   header: "Mã nhóm",
      // },
      // {
      //   accessorKey: "TenNhom",
      //   header: "Tên nhóm",
      // },
      {
        accessorKey: "tenSp",
        header: "Tên sản phẩm",
        size: 350,
      },
      {
        accessorKey: "dvt",
        header: "Đơn vị tính",
        size: 140,
        footer: "Tổng:",
        Footer(props) {
          return (
            <div>
              <Text c="red" fw={800}>
                {props.column.columnDef.footer}
              </Text>
            </div>
          );
        },
      },
      {
        accessorKey: "soLuong",
        header: "Số lượng",
        size: 130,
        Footer: ({ table }: any) => {
          const total = table
            .getRowModel()
            .rows.reduce(
              (sum: number, row: any) => sum + (row.original?.soLuong || 0),
              0
            );
          return (
            <div>
              <Text c="red" fw={800}>
                {total}
              </Text>
            </div>
          );
        },
      },
      {
        accessorKey: "ngayNhap",
        header: "Ngày nhập",
        size: 150,
        Cell: ({ renderedCellValue }: any) => (
          <>{renderedCellValue && formatDateTransfer(renderedCellValue)}</>
        ),
      },
      {
        accessorKey: "ngayNhap",
        header: "Tuổi tồn",
        size: 150,
        Cell: ({ renderedCellValue }: any) => (
          <>
            {renderedCellValue
              ? calculateDaysDifference(renderedCellValue)
              : "N/A"}
          </>
        ),
      },

      {
        accessorKey: "donGiaNhap",
        header: "Đơn giá nhập",
        size: 150,
        Cell: ({ renderedCellValue }: any) => (
          <>{formatNumber(renderedCellValue)}</>
        ),
      },
    ],
    []
  );

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => (
    <Group gap="sm">
      <Text>{dataItemCode.find((x) => x.value === option.value)?.label}</Text>
    </Group>
  );
  // Sử dụng debounce để trì hoãn việc gọi API
  const debouncedDataItemCode = useDebouncedCallback((value: string) => {
    if (value.length >= 3) {
      DataItemCode(value); // Gọi API với từ khóa tìm kiếm
    }
  }, 500);

  // Đọc file để người dùng xem trước, chỉ gọi api import khi bấm xác nhận
  const previewFile = async (e: File) => {
    open();
    try {
      const result = await readSerialImportPreview(e);
      if (result.totalRow === 0) {
        NotificationExtension.Warn("File không có dữ liệu !");
        clearFile();
        return;
      }
      setPreview(result);
    } catch (error) {
      console.log(error);
      NotificationExtension.Fails(
        "Đọc file excel thất bại, vui lòng kiểm tra lại file !"
      );
      clearFile();
    } finally {
      close();
    }
  };

  const uploadFile = async (e: File) => {
    if (!e) {
      NotificationExtension.Fails("File rỗng hoặc chưa chọn file !");
      return;
    }
    open();
    // await new Promise((resolve) => setTimeout(resolve, 2000));
    const formData = new FormData();
    formData.append("ExcelFile", e);

    try {
      const resSaveFile = await repositoryPos.post<MessageResponse<any>>(
        "/api/v1/TblInventorySerialOnHand/update-serial-status-from-excel",
        formData
      );
      if (resSaveFile && resSaveFile.success) {
        clearFile();
        NotificationExtension.Success(resSaveFile?.message);
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        NotificationExtension.Fails("Import file thất bại !");
        clearFile();
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
      clearFile();
    }
  };

  const exportDataExcel = async () => {
    open();
    try {
      let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
        pagination.pageSize
      }`;
      if (search.branchCode && search.branchCode.length > 0) {
        search.branchCode.forEach((branchCode) => {
          url += `&BranchCode=${branchCode}`;
        });
      }
      if (search.invCode && search.invCode.length > 0) {
        search.invCode.forEach((invCode) => {
          url += `&InvCode=${invCode}`;
        });
      }
      if (search.itemCode && search.itemCode.length > 0) {
        search.itemCode.forEach((itemCode) => {
          url += `&ItemCode=${itemCode}`;
        });
      }
      if (search.maChungs && search.maChungs.length > 0) {
        url += search.maChungs?.map((item) => `&MaChungs=${item}`).join("");
      }
      if (search.keySearch) {
        url += `&KeySearch=${search.keySearch.trim().toLowerCase()}`;
      }
      if (search.serial) {
        url += `&SerialList=${search.serial.trim().toLowerCase()}`;
      }
      // const baseUrl = `https://apis-pos-dev.hacom.vn/api-shipping/api/v1/ExportExcel/export-inventory-serial-report${url}`;
      // // const baseUrl = `http://192.168.68.166:5001/api/v1/ExportExcel/export-inventory-serial-onhand${url}`;
      // const fileName = "bao_cao_ton_kho_theo_ma_vach.xlsx";

      // await exportDataToExcel(baseUrl, fileName);
      const baseUrl = `/api/v1/ExportExcel/export-inventory-serial-report${url}`;

      const _url = BASE_API_DELIVERY + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });
      // Kiểm tra trạng thái HTTP
      if (!response.ok) {
        const errorResponse = await response.json(); // Parse lỗi từ API
        const errorMessage =
          errorResponse?.message || "Đã xảy ra lỗi khi xuất dữ liệu!";
        throw new Error(errorMessage); // Ném lỗi để xử lý trong `catch`
      }
      const blob = await response.blob();
      const urlss = window.URL.createObjectURL(blob);
      const _name = new Date().toISOString().slice(0, 10);
      let filename = "bao_cao_ton_kho_theo_ma_vach.xlsx";
      const link = document.createElement("a");
      link.href = urlss;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.log("error", error);
      NotificationExtension.Fails(
        error.message || "Đã xảy ra lỗi không xác định!"
      );
    } finally {
      close();
    }
  };
  // Hàm lấy dữ liệu
  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
      pagination.pageSize
    }`;
    if (search.invCode && search.invCode.length > 0) {
      search.invCode.forEach((invCode) => {
        url += `&InvCode=${invCode}`;
      });
    }
    if (search.branchCode && search.branchCode.length > 0) {
      search.branchCode.forEach((branchCode) => {
        url += `&BranchCode=${branchCode}`;
      });
    }
    if (search.itemCode && search.itemCode.length > 0) {
      search.itemCode.forEach((itemCode) => {
        url += `&ItemCode=${itemCode}`;
      });
    }
    if (search.keySearch) {
      url += `&KeySearch=${search.keySearch.trim().toLowerCase()}`;
    }
    if (search.serial) {
      url += `&SerialList=${search.serial}`;
    }
    if (search.maChungs && search.maChungs.length > 0) {
      url += search.maChungs?.map((item) => `&MaChungs=${item}`).join("");
    }
    try {
      const response = await repositoryDelivery.get(
        `/api/v1/TblInventorySerialOnhand/get-list-serial${url}`
      );
      if (response && response.success) {
        let result = response.data;
        setData(result);
        setRowCount(response.totalCount);
        table.resetRowSelection();
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const DataInvCode = async () => {
    const branchSearch = dataBranchIdSelect
      .map((item: string) => `&BranchId=${item}`)
      .join("");

    const take = search?.branchCode?.length * 100;

    const res = await repositoryDelivery.get<MessageResponse<any>>(
      `/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=${
        search?.branchCode && search?.branchCode?.length > 0 ? take : 100
      }${branchSearch}`
    );

    if (res && res.success) {
      const result = res.data;
      const data = result
        ?.filter((item: any) => item.idBranch && item.name)
        ?.map((item: any) => ({
          label: `${item.code} - ${item.name}`,
          value: item.code?.toString(),
        }));
      setDataInvCode(data);
    }
  };

  const DataBrachCode = async () => {
    try {
      const response = await repositoryPos.get(
        `api/v1/TblDmInventory/get-select-branch?auth=true`
      );
      if (response && response.success) {
        const transformedData = response.data
          .filter((i: any) => i.att2 && i.text)
          .map((item: any) => {
            return {
              value: item.att2,
              label: item.att2 + " - " + item.text,
              id: item.value,
            };
          });

        setDataBranchCode(transformedData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const DataItemCode = async (keySearch: string) => {
    try {
      let url = "";
      if (keySearch && keySearch.length > 0) {
        url += `&ItemCodes=${keySearch}`;
      }
      // const response = await repositoryDelivery.get(
      //   `api/v1/TblInventorySerialOnhand/get-list-item-by-inv?Skip=0&Take=50` +
      //     url
      // );
      const response = await repositoryPos.get(
        `/api/v1/TblItem/get-select?Key=${keySearch}&Page=30`
      );
      if (response && response.success) {
        // const transformedData = response.data
        //   ?.filter((i: any) => i.maSp && i.tenSp)
        //   .map((branch: any) => ({
        //     value: branch.maSp,
        //     label: branch.maSp + " - " + branch.tenSp,
        //   }));
        const transformedData = response.data
          ?.filter((i: any) => i.value && i.text)
          .map((branch: any) => ({
            value: branch.value,
            label: branch.text,
          }));
        const uniqueData = Array.from(
          new Set(transformedData.map((item: any) => item.value))
        ).map((value) =>
          transformedData.find((item: any) => item.value === value)
        );
        setDataItemCode(uniqueData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const fetchCatSegment4d = async () => {
    let url = "/api/v1/TblCatSegment4d/get-list?Skip=0&Take=20";

    if (searchCatSegment4d && searchCatSegment4d.length > 0) {
      url += `&CodeString=${searchCatSegment4d}`;
    }

    const res = await repositoryPos.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const dataAPI = res.data
        ?.filter((item: any) => item.codeString !== null)
        ?.map((item: any) => ({
          label: `${item.codeString} - ${item.name}`,
          value: item.codeString?.toString(),
        }));
      setDataCatSegment4d(dataAPI);
    }
  };

  useEffect(() => {
    fetchCatSegment4d();
  }, []);

  useEffect(() => {
    fetchData();
  }, [pagination]);

  useEffect(() => {
    const listId = dataBranchCode
      ?.filter((item) => (search?.branchCode as string[])?.includes(item.value))
      ?.map((item) => item.id);

    setDataBranchIdSelect(listId);
  }, [search.branchCode]);

  useEffect(() => {
    DataInvCode();
  }, [dataBranchIdSelect]);

  useEffect(() => {
    if (debounceCatSegment4d && debounceCatSegment4d.length > 4) {
      fetchCatSegment4d();
    }
  }, [debounceCatSegment4d]);

  useEffect(() => {
    if (file) {
      if (
        file &&
        (file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "application/vnd.ms-excel")
      )
        previewFile(file);
      else NotificationExtension.Fails("File không đúng định đạng Excel !");
    }
  }, [file]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (155 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    // manualPagination: true,
    rowCount,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    onPaginationChange: setPagination,
    positionToolbarAlertBanner: "bottom",
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["STT", "maSp"],
        right: ["statusName"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTopToolbarProps: {
      style: {
        display: "none",
      },
    },
    localization: _localization,
    getRowId: (row) => row.departmentId,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
  });

  return (
    <Box>
      <LoadingOverlayCustom visible={visible} />
      <ImportSerialPreviewModal
        preview={preview}
        onClose={() => {
          setPreview(null);
          clearFile();
        }}
        onConfirm={() => {
          setPreview(null);
          if (file) uploadFile(file);
        }}
      />
      <div
        style={{ border: "1px solid #dee2e6", padding: "10px" }}
        ref={headerRef}
      >
        <Group justify="space-between" gap={0}>
          <_breadcrumb></_breadcrumb>
          <Flex align={"center"} gap={"xs"}>
            <Button
              leftSection={<IconDownload size={14} />}
              color="teal"
              variant="outline"
              component="a"
              href="/template/SerialStatusImportTemplate.xlsx"
              download
            >
              Tải file mẫu
            </Button>
            <FileButton resetRef={resetRef} onChange={setFile}>
              {(props) => (
                <Button
                  leftSection={
                    <IconFileUpload
                      style={{ width: rem(14), height: rem(14) }}
                    />
                  }
                  {...props}
                >
                  Import Excel
                </Button>
              )}
            </FileButton>
            <Button
              leftSection={
                <IconFileDownload style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={() => exportDataExcel()}
            >
              Export Excel
            </Button>
          </Flex>
        </Group>
        <Grid mt={10}>
          <Grid.Col span={sizeGrid}>
            <Input
              placeholder="Nhập tìm kiếm"
              type="text"
              leftSection={<IconSearch size={"20"} color="#15aabf" />}
              onChange={(e) => {
                handleChangeSearch(e.target.value ?? "", "keySearch");
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              size="sm"
              placeholder="Nhập mã sản phẩm"
              clearable
              searchable
              data={dataItemCode.map((x) => x.value)} // Hiển thị kết quả từ API
              value={search.itemCode}
              renderOption={renderMultiSelectOption}
              leftSection={<IconBarcode size={20} color="#15aabf" />}
              // onSearchChange={(value) => {
              //   if (value.length >= 3) {
              //     DataItemCode(value); // Gọi API với từ khóa tìm kiếm
              //   }
              // }}
              onSearchChange={(value) => {
                debouncedDataItemCode(value); // Sử dụng debounce
              }}
              onChange={(value) => {
                handleChangeSearchValue(value, "itemCode");
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Input
              placeholder="Nhập mã vạch"
              type="text"
              value={search.serial}
              leftSection={<IconSearch size={"20"} color="#15aabf" />}
              onChange={(e) => {
                handleChangeSearch(e.target.value ?? "", "serial");
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              placeholder="Chi nhánh"
              clearable
              searchable
              value={search.branchCode}
              data={dataBranchCode?.map((item) => ({
                label: item.label,
                value: item.value,
              }))}
              leftSection={<IconBuildingSkyscraper size={20} color="#15aabf" />}
              onChange={(value) => {
                handleChangeSearchValue(value ? value : null, "branchCode");
              }}
              onClick={() => {
                if (dataBranchCode.length === 0) {
                  DataBrachCode();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              size="sm"
              placeholder="Mã kho"
              searchable
              clearable
              data={dataInvCode}
              value={search.invCode}
              leftSection={<IconBuildingWarehouse size={20} color="#15aabf" />}
              onChange={(value) => {
                handleChangeSearchValue(value, "invCode");

                // const _key = value.target.value ?? ""; // Nếu value là mảng
                // setKeySearch(_key);
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              size="sm"
              placeholder="Nhập mã chủng"
              clearable
              searchable
              searchValue={searchCatSegment4d}
              onSearchChange={(e) => setSearchCatSegment4d(e)}
              data={dataCatSegment4d}
              value={search.maChungs}
              onChange={(value) => {
                handleChangeSearchValue(value, "maChungs");
              }}
              leftSection={<IconBarcode size={20} color="#15aabf" />}
              nothingFoundMessage={"Không tìm thấy dữ liệu !"}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Button
              color="blue"
              variant="outline"
              leftSection={<IconSearch size={16} />}
              onClick={async () => {
                await fetchData();
              }}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export default ListSerialStocktaking;
